import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  TextField,
  MenuItem,
  Box,
  Typography,
  Card,
  CardMedia,
  CardActions,
  IconButton,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { siteDocuments } from '../services/api';

function SiteIdentity({ data, onChange, onImageChange, batiments, setBatiments, userRole, userProvince, siteId }) {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [docLoading, setDocLoading] = useState(false);
  const [docSnackbar, setDocSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Créer une URL de prévisualisation quand un fichier est sélectionné
  useEffect(() => {
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [selectedFile]);

  // Charger les documents du site
  const loadDocuments = useCallback(async () => {
    if (!siteId) return;
    try {
      const response = await siteDocuments.getBySite(siteId);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  }, [siteId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !siteId) return;
    e.target.value = '';
    setDocLoading(true);
    try {
      await siteDocuments.upload(siteId, file);
      setDocSnackbar({ open: true, message: t('documentUploaded'), severity: 'success' });
      await loadDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      setDocSnackbar({ open: true, message: t('documentUploadError'), severity: 'error' });
    } finally {
      setDocLoading(false);
    }
  };

  const handleDocumentView = async (doc) => {
    try {
      const response = await siteDocuments.download(siteId, doc.id);
      const blob = new Blob([response.data], { type: doc.mime_type });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error viewing document:', error);
    }
  };

  const handleDocumentDownload = async (doc) => {
    try {
      const response = await siteDocuments.download(siteId, doc.id);
      const blob = new Blob([response.data], { type: doc.mime_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
    }
  };

  const handleDocumentDelete = async (doc) => {
    if (!window.confirm(t('confirmDeleteDocument'))) return;
    try {
      await siteDocuments.delete(siteId, doc.id);
      setDocSnackbar({ open: true, message: t('documentDeleted'), severity: 'success' });
      await loadDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      setDocSnackbar({ open: true, message: t('documentDeleteError'), severity: 'error' });
    }
  };

  const getDocIcon = (mimeType) => {
    if (mimeType === 'application/pdf') return <PdfIcon color="error" />;
    return <ImageIcon color="primary" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      onImageChange(file);
    }
  };

  const handleDeleteImage = () => {
    // Supprimer l'image en mettant image_path à null
    onChange('image_path', null);
    setSelectedFile(null);
    setPreviewUrl(null);
    // Informer le parent que l'image a été supprimée
    if (onImageChange) {
      onImageChange(null);
    }
  };

  // Déterminer quelle image afficher (nouvelle sélection ou existante)
  const getImageUrl = () => {
    if (previewUrl) {
      return previewUrl;
    }
    if (data.image_path) {
      // image_path contient déjà "/uploads/filename.ext"
      return `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${data.image_path}`;
    }
    return null;
  };

  const imageUrl = getImageUrl();
  const hasImage = imageUrl !== null;

  // Synchroniser les bâtiments avec le nombre de bâtiments
  const numberOfBuildings = parseInt(data.nombre_batiments) || 0;

  // Mettre à jour les bâtiments si le nombre a changé
  React.useEffect(() => {
    if (setBatiments) {
      const currentCount = batiments ? batiments.length : 0;

      if (numberOfBuildings > currentCount) {
        // Ajouter des bâtiments
        const newBatiments = [...(batiments || [])];
        for (let i = currentCount; i < numberOfBuildings; i++) {
          newBatiments.push({
            numero_batiment: i + 1,
            type_batiment: '',
            usage_batiment: '',
            nombre_etages: '',
            surface_batiment: ''
          });
        }
        setBatiments(newBatiments);
      } else if (numberOfBuildings < currentCount) {
        // Supprimer des bâtiments
        setBatiments(batiments.slice(0, numberOfBuildings));
      }
    }
  }, [numberOfBuildings]);

  const updateBatiment = (index, field, value) => {
    if (setBatiments && batiments) {
      const newBatiments = [...batiments];
      newBatiments[index] = { ...newBatiments[index], [field]: value };
      setBatiments(newBatiments);
    }
  };

  // Générer les champs dynamiques pour les bâtiments
  const renderBuildingFields = () => {
    const fields = [];

    if (batiments) {
      batiments.forEach((batiment, index) => {
        fields.push(
          <Grid item xs={12} key={`building-header-${index}`}>
            <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 600, color: 'primary.main' }}>
              {t('building')} {index + 1}
            </Typography>
          </Grid>,
          <Grid item xs={12} md={6} key={`type-${index}`}>
            <TextField
              fullWidth
              select
              label={`${t('buildingType')} ${index + 1}`}
              value={batiment.type_batiment || ''}
              onChange={(e) => updateBatiment(index, 'type_batiment', e.target.value)}
            >
              <MenuItem value="maison_accueil">{t('buildingTypes.maison_accueil')}</MenuItem>
              <MenuItem value="maison_retraite">{t('buildingTypes.maison_retraite')}</MenuItem>
              <MenuItem value="maison_cimetiere">{t('buildingTypes.maison_cimetiere')}</MenuItem>
              <MenuItem value="chapelle">{t('buildingTypes.chapelle')}</MenuItem>
              <MenuItem value="eglise">{t('buildingTypes.eglise')}</MenuItem>
              <MenuItem value="dependance">{t('buildingTypes.dependance')}</MenuItem>
              <MenuItem value="autre">{t('buildingTypes.autre')}</MenuItem>
            </TextField>
          </Grid>,
          <Grid item xs={12} md={6} key={`usage-${index}`}>
            <TextField
              fullWidth
              label={`${t('buildingUsage')} ${index + 1}`}
              value={batiment.usage_batiment || ''}
              onChange={(e) => updateBatiment(index, 'usage_batiment', e.target.value)}
            />
          </Grid>,
          <Grid item xs={12} md={6} key={`surface-${index}`}>
            <TextField
              fullWidth
              type="number"
              label={`${t('buildingSurface')} ${index + 1}`}
              value={batiment.surface_batiment || ''}
              onChange={(e) => updateBatiment(index, 'surface_batiment', e.target.value)}
            />
          </Grid>,
          <Grid item xs={12} md={6} key={`floors-${index}`}>
            <TextField
              fullWidth
              type="number"
              label={`${t('floors')} ${index + 1}`}
              value={batiment.nombre_etages || ''}
              onChange={(e) => updateBatiment(index, 'nombre_etages', e.target.value)}
            />
          </Grid>
        );
      });
    }

    return fields;
  };

  // Vérifier si les champs de location doivent être affichés
  const showRentalFields = !(data.statut_juridique === 'proprietaire' && data.statut_occupation === 'occupant');

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={t('siteName')}
          value={data.nom_site || ''}
          onChange={(e) => onChange('nom_site', e.target.value)}
          required
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={t('address')}
          value={data.adresse || ''}
          onChange={(e) => onChange('adresse', e.target.value)}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={t('city')}
          value={data.ville || ''}
          onChange={(e) => onChange('ville', e.target.value)}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={t('country')}
          value={data.pays || ''}
          onChange={(e) => onChange('pays', e.target.value)}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          select
          label={t('province')}
          value={data.province || ''}
          onChange={(e) => onChange('province', e.target.value)}
          disabled={userRole === 'admin_province'}
        >
          <MenuItem value="bresil">{t('provinces.bresil')}</MenuItem>
          <MenuItem value="region_asie">{t('provinces.region_asie')}</MenuItem>
          <MenuItem value="madagascar">{t('provinces.madagascar')}</MenuItem>
          <MenuItem value="europe_togo">{t('provinces.europe_togo')}</MenuItem>
          <MenuItem value="amerique_nord">{t('provinces.amerique_nord')}</MenuItem>
          <MenuItem value="nouvelle_zelande">{t('provinces.nouvelle_zelande')}</MenuItem>
          <MenuItem value="generalat">{t('provinces.generalat')}</MenuItem>
        </TextField>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          select
          label={t('legalStatus')}
          value={data.statut_juridique || 'proprietaire'}
          onChange={(e) => onChange('statut_juridique', e.target.value)}
        >
          <MenuItem value="proprietaire">{t('owner')}</MenuItem>
          <MenuItem value="locataire">{t('tenant')}</MenuItem>
        </TextField>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          select
          label={t('occupationStatus')}
          value={data.statut_occupation || ''}
          onChange={(e) => onChange('statut_occupation', e.target.value)}
        >
          <MenuItem value="occupant">{t('occupationStatuses.occupant')}</MenuItem>
          <MenuItem value="occupant_partiel">{t('occupationStatuses.occupantPartiel')}</MenuItem>
          <MenuItem value="mise_en_location">{t('occupationStatuses.miseEnLocation')}</MenuItem>
        </TextField>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          select
          label={t('easement')}
          value={data.servitude || 'inconnu'}
          onChange={(e) => onChange('servitude', e.target.value)}
        >
          <MenuItem value="oui">{t('yes')}</MenuItem>
          <MenuItem value="non">{t('no')}</MenuItem>
          <MenuItem value="inconnu">{t('unknown')}</MenuItem>
        </TextField>
      </Grid>

      {data.statut_juridique === 'proprietaire' && (
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="number"
            label={t('acquisitionYear')}
            value={data.annee_acquisition || ''}
            onChange={(e) => onChange('annee_acquisition', e.target.value)}
          />
        </Grid>
      )}

      {showRentalFields && (
        <>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label={t('rentedSince')}
              value={data.date_location_debut || ''}
              onChange={(e) => onChange('date_location_debut', e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label={t('rentedUntil')}
              value={data.date_location_fin || ''}
              onChange={(e) => onChange('date_location_fin', e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        </>
      )}

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label={t('gardenSurface')}
          value={data.surface_jardins || ''}
          onChange={(e) => onChange('surface_jardins', e.target.value)}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label={t('residentsCount')}
          value={data.nombre_personnes_residentes || ''}
          onChange={(e) => onChange('nombre_personnes_residentes', e.target.value)}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label={t('employeesCount')}
          value={data.nombre_employes || ''}
          onChange={(e) => onChange('nombre_employes', e.target.value)}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label={t('guestsCount')}
          value={data.nombre_personnes_accueillis || ''}
          onChange={(e) => onChange('nombre_personnes_accueillis', e.target.value)}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          select
          label={t('currency')}
          value={data.devise_pays || ''}
          onChange={(e) => onChange('devise_pays', e.target.value)}
        >
          <MenuItem value="EUR">{t('currencies.EUR')}</MenuItem>
          <MenuItem value="BRL">{t('currencies.BRL')}</MenuItem>
          <MenuItem value="MGA">{t('currencies.MGA')}</MenuItem>
          <MenuItem value="PHP">{t('currencies.PHP')}</MenuItem>
          <MenuItem value="NZD">{t('currencies.NZD')}</MenuItem>
          <MenuItem value="USD">{t('currencies.USD')}</MenuItem>
          <MenuItem value="MOP">{t('currencies.MOP')}</MenuItem>
          <MenuItem value="SGD">{t('currencies.SGD')}</MenuItem>
          <MenuItem value="XOF">{t('currencies.XOF')}</MenuItem>
          <MenuItem value="GBP">{t('currencies.GBP')}</MenuItem>
          <MenuItem value="AUD">{t('currencies.AUD')}</MenuItem>
        </TextField>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label={t('buildings')}
          value={data.nombre_batiments || ''}
          onChange={(e) => onChange('nombre_batiments', e.target.value)}
        />
      </Grid>

      {renderBuildingFields()}

      <Grid item xs={12}>
        <Box>
          <Typography variant="subtitle1" gutterBottom fontWeight={600}>
            {t('image')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('sitePhotoDescription')}
          </Typography>

          {/* Affichage de l'image existante ou prévisualisée */}
          {hasImage && (
            <Card sx={{ maxWidth: 600, mt: 2, mb: 3 }}>
              <CardMedia
                component="img"
                height="300"
                image={imageUrl}
                alt={data.nom_site}
                sx={{ objectFit: 'contain', bgcolor: '#f5f5f5' }}
              />
              <CardActions sx={{ justifyContent: 'space-between', p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {selectedFile && (
                    <Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CheckCircleIcon fontSize="small" />
                      {t('newImageSelected') || 'Nouvelle image sélectionnée'}
                    </Typography>
                  )}
                </Box>
                <IconButton
                  color="error"
                  onClick={handleDeleteImage}
                  aria-label="delete image"
                >
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          )}

          {/* Champ d'upload amélioré */}
          <Paper
            variant="outlined"
            component="label"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 4,
              cursor: 'pointer',
              borderStyle: 'dashed',
              borderWidth: 2,
              borderColor: selectedFile ? 'success.main' : 'divider',
              bgcolor: selectedFile ? 'success.lighter' : 'background.default',
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
              maxWidth: 600,
            }}
          >
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleFileChange}
            />
            {selectedFile ? (
              <>
                <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                <Typography variant="body1" fontWeight={600} color="success.main">
                  {selectedFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  {t('clickToChangeImage') || 'Cliquez pour changer l\'image'}
                </Typography>
              </>
            ) : (
              <>
                <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" fontWeight={600}>
                  {hasImage ? (t('changeImage') || 'Changer l\'image') : (t('uploadImage') || 'Télécharger une image')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('dragDropOrClick') || 'Cliquez pour sélectionner une image'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  PNG, JPG, JPEG (max 10 MB)
                </Typography>
              </>
            )}
          </Paper>
        </Box>
      </Grid>

      {/* Section Gestion documentaire */}
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Box>
          <Typography variant="subtitle1" gutterBottom fontWeight={600}>
            {t('siteDocuments')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('siteDocumentsDescription')}
          </Typography>

          {!siteId ? (
            <Alert severity="info" sx={{ maxWidth: 600 }}>
              {t('saveSiteFirst')}
            </Alert>
          ) : (
            <>
              {/* Zone d'upload de document */}
              <Paper
                variant="outlined"
                component="label"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                  cursor: docLoading ? 'wait' : 'pointer',
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  borderColor: 'divider',
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover',
                  },
                  maxWidth: 600,
                  mb: 2,
                }}
              >
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,application/pdf"
                  onChange={handleDocumentUpload}
                  disabled={docLoading}
                />
                {docLoading ? (
                  <CircularProgress size={32} />
                ) : (
                  <>
                    <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body1" fontWeight={600}>
                      {t('addDocument')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      {t('addDocumentHint')}
                    </Typography>
                  </>
                )}
              </Paper>

              {/* Liste des documents */}
              {documents.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {t('noDocuments')}
                </Typography>
              ) : (
                <List sx={{ maxWidth: 600, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  {documents.map((doc, index) => (
                    <ListItem
                      key={doc.id}
                      divider={index < documents.length - 1}
                      sx={{ pr: 16 }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {getDocIcon(doc.mime_type)}
                      </ListItemIcon>
                      <ListItemText
                        primary={doc.original_name}
                        secondary={formatFileSize(doc.file_size)}
                        primaryTypographyProps={{ noWrap: true, sx: { maxWidth: 300 } }}
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title={t('viewFile')}>
                          <IconButton edge="end" onClick={() => handleDocumentView(doc)} sx={{ mr: 0.5 }}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('downloadFile')}>
                          <IconButton edge="end" onClick={() => handleDocumentDownload(doc)} sx={{ mr: 0.5 }}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('deleteFile')}>
                          <IconButton edge="end" onClick={() => handleDocumentDelete(doc)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          )}
        </Box>
      </Grid>

      <Snackbar
        open={docSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setDocSnackbar({ ...docSnackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setDocSnackbar({ ...docSnackbar, open: false })} severity={docSnackbar.severity} sx={{ width: '100%' }}>
          {docSnackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

export default SiteIdentity;
